import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import CombinedCommentsWidget from '../../components/dashboard/CombinedCommentsWidget';
import StatusBadge from '../../components/ui/StatusBadge';
import CapacityBar, { CapacityBarMini } from '../../components/ui/CapacityBar';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db, COLLECTIONS } from '../../firebase/config';
import DailyRoutinesWidget from '../../components/dashboard/DailyRoutinesWidget';
import SimpleTaskForm from '../../components/SimpleTaskForm';
import { Trash2 } from 'lucide-react';
import { 
  Users, 
  FolderOpen, 
  CheckSquare, 
  AlertTriangle,
  Clock,
  Calendar,
  X,
  Plus,
  FileText,
  Globe,
  Building2,
  User,
  Edit,
  RotateCcw,
  Activity,
  Sun,
  Moon,
  ClipboardList
} from 'lucide-react';
import TabsComponent from '../../components/ui/TabsComponent';
import { useNavigate } from 'react-router-dom';

import { renderTasksContent, renderProjectsContent, renderTeamsContent, renderDepartmentsContent } from '../../components/dashboard/InlineContent';

const DeputyCeoDashboard = ({ activeTab, setActiveTab }) => {
  const { currentUser } = useAuth();
  const { tasks = [], projects = [], users = [], departments = [], teams = [],
          updateProject, updateTask, createProject, createTask, deleteProject, deleteTask,
          createTeam, updateTeam, deleteTeam } = useData();
  const navigate = useNavigate();

  // State for filters
  const [filterProjectStatus, setFilterProjectStatus] = useState('all');
  const [filterTaskStatus, setFilterTaskStatus] = useState('all');
  const [filterTaskPriority, setFilterTaskPriority] = useState('all');
  const [filterProjectScope, setFilterProjectScope] = useState('all');
  const [showCompletedProjects, setShowCompletedProjects] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  
  // State for inline content display
  const [selectedContent, setSelectedContent] = useState(null); // 'tasks', 'projects', 'teams', 'departments'
  const [selectedTab, setSelectedTab] = useState('management'); // 'management', 'personal'
  useEffect(() => {
    setSelectedContent(null);
    setSelectedTab(activeTab);
  }, [activeTab]);




  // Function to handle box clicks
  const handleBoxClick = (contentType, tabType) => {
    setSelectedContent(contentType);
    setSelectedTab(tabType);
  };

  // Render inline content based on selection
  const renderInlineContent = () => {
    if (!selectedContent) return null;

    const isPersonal = selectedTab === 'personal';
    
    switch (selectedContent) {
      case 'tasks':
        return renderTasksContent(tasks, users, currentUser, isPersonal, setSelectedContent, navigate);
      case 'projects':
        return renderProjectsContent(projects, tasks, users, currentUser, isPersonal, setSelectedContent, navigate);
      case 'teams':
        return renderTeamsContent(teams, users, tasks, setSelectedContent, navigate);
      case 'departments':
        return renderDepartmentsContent(departments, users, tasks, setSelectedContent, navigate, currentUser);
      default:
        return null;
    }
  };

  // State for modals
  const [showEditProject, setShowEditProject] = useState(false);
  const [showEditTask, setShowEditTask] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showNewDepartment, setShowNewDepartment] = useState(false);
  const [showNewTeam, setShowNewTeam] = useState(false);
  const [showEditTeam, setShowEditTeam] = useState(false);
  const [editTeam, setEditTeam] = useState(null);

  // State for new forms
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    scope: 'department',
    priority: 'important_not_urgent',
    startDate: '',
    endDate: '',
    teamMembers: []
  });
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigneeId: '',
    priority: 'important_not_urgent',
    dueDate: '',
    estimate: 0,
    projectId: ''
  });
  const [newDepartment, setNewDepartment] = useState({
    name: '',
    description: '',
    managerId: '',
    isActive: true
  });
  const [newTeam, setNewTeam] = useState({
    name: '',
    description: '',
    departmentId: '',
    managerId: '',
    memberIds: [],
    isActive: true
  });

  // Helper functions
  const shouldHideTask = (task) => {
    // הסתר משימות שנוצרו משגרות יומיות - הן צריכות להופיע רק בעמוד השגרות
    if (task.isDailyRoutine || task.routineId || task.createdFrom === 'daily_routine') {
      return true;
    }
    
    // בדיקה נוספת - משימות שהשם שלהן מכיל תאריך (סימן למשימת שגרה)
    if (task.title && task.title.includes(' - ') && task.title.match(/\d{1,2}\/\d{1,2}\/\d{4}/)) {
      return true;
    }
    
    const taskProject = task.projectId ? projects.find(p => p.id === task.projectId) : null;
    const hidePersonalTask = task.scope === 'personal' && task.assigneeId !== currentUser?.id && task.createdBy !== currentUser?.id;
    const hidePersonalProjectTask = taskProject && taskProject.scope === 'personal' && taskProject.assigneeId !== currentUser?.id && taskProject.createdBy !== currentUser?.id;
    return hidePersonalTask || hidePersonalProjectTask;
  };

  const getProjectStatus = (project) => {
    if (!project) return 'לא ידוע';
    const projectTasks = tasks.filter(t => t.projectId === project.id);
    if (projectTasks.length === 0) return 'ממתין לעבודה';
    const completedTasks = projectTasks.filter(t => t.status === 'הושלם').length;
    const totalTasks = projectTasks.length;
    if (completedTasks === 0) return 'ממתין לעבודה';
    if (completedTasks === totalTasks) return 'פרויקט הושלם';
    return 'פרויקט בתהליך';
  };

  // Filter projects and tasks
  const filteredDashboardProjects = projects.filter(project => {
    let hasAccess = currentUser?.role === 'deputy_ceo';
    if (!hasAccess) return false;
    
    const matchesScope = filterProjectScope === 'all' || project.scope === filterProjectScope;
    const projectStatus = getProjectStatus(project);
    const matchesStatus = filterProjectStatus === 'all' || 
                         (filterProjectStatus === 'completed' && projectStatus === 'פרויקט הושלם') ||
                         projectStatus === filterProjectStatus;
    
    if (showCompletedProjects && projectStatus !== 'פרויקט הושלם') return false;
    if (!showCompletedProjects && projectStatus === 'פרויקט הושלם') return false;
    
    return matchesScope && matchesStatus;
  });

  const filteredDashboardTasks = tasks.filter(task => {
    if (shouldHideTask(task)) return false;
    
    // Additional filter: Only show organizational tasks for deputy CEO
    if (currentUser?.role === 'deputy_ceo') {
      // Show only organizational tasks (not personal of others)
      if (task.scope === 'personal' && task.assigneeId !== currentUser?.id && task.createdBy !== currentUser?.id) {
        return false;
      }
    }
    
    const matchesStatus = filterTaskStatus === 'all' || task.status === filterTaskStatus;
    const matchesPriority = filterTaskPriority === 'all' || task.priority === filterTaskPriority;
    if (showCompletedTasks && task.status !== 'הושלם') return false;
    if (!showCompletedTasks && task.status === 'הושלם') return false;
    return matchesStatus && matchesPriority;
  });

  const unacknowledgedTasks = tasks.filter(task => {
    if (!task || task.acknowledged) return false;
    if (currentUser?.role === 'deputy_ceo') {
      return (task.assigneeId === currentUser?.id || 
             (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(currentUser?.id)) ||
             task.createdBy === currentUser?.id) && !shouldHideTask(task);
    }
    return false;
  }) || [];

  const atRiskTasks = tasks.filter(task => {
    if (!task || (task.status !== 'בסיכון' && task.status !== 'חסום')) return false;
    if (currentUser?.role === 'deputy_ceo') {
      return task.departmentId === currentUser?.departmentId;
    }
    return false;
  }) || [];

  // Stats card component
  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      red: 'bg-red-50 text-red-600'
    };

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <p className="text-2xl font-bold mt-1">{value || 0}</p>
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon size={24} />
          </div>
        </div>
      </div>
    );
  };

  // Tabs configuration
  const tabs = (() => {
    const managementTab = {
      id: 'management',
      label: 'ניהול',
      icon: Building2,
      badge: departments.length.toString(),
      content: (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">ניהול פרויקטים ומשימות החברה</h2>
            <p className="text-sm text-gray-600 mt-1">סקירה כללית של הפרויקטים והמשימות בארגון</p>
          </div>
          <div className="p-6">
            {/* Overview cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Departments Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => navigate('/departments')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <Building2 size={32} className="text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">מחלקות</h3>
                  <p className="text-3xl font-bold text-blue-600 mb-2">{departments.length}</p>
                  <p className="text-sm text-gray-600">סה"כ מחלקות</p>
                </div>
              </div>

              {/* Projects Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('projects', 'management')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <FolderOpen size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">פרויקטים</h3>
                  <p className="text-3xl font-bold text-green-600 mb-2">{filteredDashboardProjects.length}</p>
                  <p className="text-sm text-gray-600">פרויקטים פעילים</p>
                </div>
              </div>

              {/* Tasks Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('tasks', 'management')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <CheckSquare size={32} className="text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">משימות</h3>
                  <p className="text-3xl font-bold text-purple-600 mb-2">{filteredDashboardTasks.length}</p>
                  <p className="text-sm text-gray-600">משימות פעילות</p>
                </div>
              </div>

              {/* Teams Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('teams', 'management')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <Users size={32} className="text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">צוותים</h3>
                  <p className="text-3xl font-bold text-orange-600 mb-2">{teams?.length || 0}</p>
                  <p className="text-sm text-gray-600">סה"כ צוותים</p>
                </div>
              </div>
            </div>

             {/* Teams Management Section */}
             <div className="mt-8 pt-6 border-t">
               <div className="flex items-center justify-between mb-4">
                 <h3 className="text-lg font-medium">ניהול צוותים</h3>
                 <button
                   onClick={() => setShowNewTeam(true)}
                   className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                 >
                   <Plus size={16} />
                   צור צוות חדש
                 </button>
               </div>
               
               {teams.length === 0 ? (
                 <p className="text-gray-500 text-center py-8">אין צוותים עדיין</p>
               ) : (
                 <div className="overflow-x-auto">
                   <table className="w-full">
                     <thead className="bg-gray-50">
                       <tr>
                         <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">שם צוות</th>
                         <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">תיאור</th>
                         <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מחלקה</th>
                         <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מנהל צוות</th>
                         <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">חברי צוות</th>
                         <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">סטטוס</th>
                         <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">פעולות</th>
                       </tr>
                     </thead>
                     <tbody className="bg-white divide-y divide-gray-200">
                       {teams.map((team) => {
                         const teamManager = users.find(u => u.id === team.managerId);
                         const teamMembers = users.filter(u => team.memberIds?.includes(u.id) || []);
                         const teamDepartment = departments.find(d => d.id === team.departmentId);
                         
                         return (
                           <tr key={team.id} className="hover:bg-gray-50">
                             <td 
                               className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 cursor-pointer"
                               onClick={() => navigate(`/teams/${team.id}`)}
                               title="לחץ לצפייה בפרטי הצוות"
                             >
                               {team.name}
                             </td>
                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                               {team.description || '-'}
                             </td>
                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                               {teamDepartment?.name || '-'}
                             </td>
                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                               {teamManager?.name || '-'}
                             </td>
                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                               {teamMembers.length > 0 ? `(${teamMembers.length} עובדים)` : '-'}
                             </td>
                             <td className="px-6 py-4 whitespace-nowrap">
                               <StatusBadge 
                                 status={team.isActive ? 'פעיל' : 'לא פעיל'} 
                                 size="sm" 
                               />
                             </td>
                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                               <div className="flex items-center gap-2">
                                 <button
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     setEditTeam(team);
                                     setShowEditTeam(true);
                                   }}
                                   className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                   title="ערוך צוות"
                                 >
                                   ✏️
                                 </button>
                                 <button
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     if (window.confirm('האם אתה בטוח שברצונך למחוק את הצוות הזה?')) {
                                       deleteTeam(team.id);
                                     }
                                   }}
                                   className="p-1 text-red-600 hover:bg-red-100 rounded"
                                   title="מחק צוות"
                                 >
                                   🗑️
                                 </button>
                               </div>
                             </td>
                           </tr>
                         );
                       })}
                     </tbody>
                   </table>
                 </div>
               )}
             </div>

             {/* Quick Actions */}
             <div className="mt-8 pt-6 border-t">
               <h3 className="text-lg font-medium mb-4">פעולות מהירות</h3>
               <div className="flex flex-wrap gap-3">
                 <button
                   onClick={() => navigate('/admin')}
                   className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
                 >
                   <Users size={16} />
                   ניהול מערכת
                 </button>
                 <button
                   onClick={() => navigate('/admin?tab=users')}
                   className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                 >
                   <User size={16} />
                   ניהול משתמשים
                 </button>
                 <button
                   onClick={() => navigate('/teams')}
                   className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                 >
                   <Users size={16} />
                   ניהול צוותים
                 </button>
                 <button
                   onClick={() => navigate('/tasks')}
                   className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                 >
                   <CheckSquare size={16} />
                   ניהול משימות
                 </button>
                 <button
                   onClick={() => navigate('/projects')}
                   className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                 >
                   <FolderOpen size={16} />
                   ניהול פרויקטים
                 </button>
                 <button
                   onClick={() => navigate('/departments')}
                   className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                 >
                   <Building2 size={16} />
                   ניהול מחלקות
                 </button>
                 <button
                   onClick={() => navigate('/templates')}
                   className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
                 >
                   <FileText size={16} />
                   ניהול תבניות
                 </button>
                 <button
                   onClick={() => navigate('/audit-log')}
                   className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                 >
                   <ClipboardList size={16} />
                   יומן פעילות
                 </button>
                 <button
                   onClick={() => navigate('/user-dashboards')}
                   className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                 >
                   <Users size={16} />
                   דשבורדים של עובדים
                 </button>
               </div>
             </div>
          </div>
        </div>
      )
    };

    const personalTab = {
      id: 'personal',
      label: 'אישי',
      icon: User,
      badge: (() => {
            const personalProjects = projects.filter(p => p.scope === 'personal');
    const personalTasks = tasks.filter(t => t.scope === 'personal');
        return (personalProjects.length + personalTasks.length).toString();
      })(),
      content: (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">פרויקטים ומשימות אישיים</h2>
            <p className="text-sm text-gray-600 mt-1">ניהול הפרויקטים והמשימות האישיים שלך</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Projects Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('projects', 'personal')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <FolderOpen size={32} className="text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">פרויקטים אישיים</h3>
                  <p className="text-3xl font-bold text-blue-600 mb-2">
                    {(() => {
                      const personalProjects = projects.filter(p => p.scope === 'personal');
                      return personalProjects.length;
                    })()}
                  </p>
                  <p className="text-sm text-gray-600">פרויקטים אישיים</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const personalProjects = projects.filter(p => p.scope === 'personal');
                      const activeProjects = personalProjects.filter(p => {
                        const pts = tasks.filter(t => t.projectId === p.id);
                        const done = pts.filter(t => t.status === 'הושלם').length;
                        return pts.length === 0 || done < pts.length;
                      });
                      const completedProjects = personalProjects.filter(p => {
                        const pts = tasks.filter(t => t.projectId === p.id);
                        const done = pts.filter(t => t.status === 'הושלם').length;
                        return pts.length > 0 && done === pts.length;
                      });
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeProjects.length} פעילים
                          </div>
                          <div className="text-center py-1 text-blue-600">
                            {completedProjects.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Personal Tasks Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('tasks', 'personal')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <CheckSquare size={32} className="text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">משימות אישיות</h3>
     <p className="text-3xl font-bold text-purple-600 mb-2">
  {(() => {
    const personalTasks = tasks.filter(t => t.scope === 'personal');
    return personalTasks.length;
  })()}
</p>
                  <p className="text-sm text-gray-600">משימות אישיות</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const personalTasks = tasks.filter(t => t.scope === 'personal');
                      const activeTasks = personalTasks.filter(t => t.status !== 'הושלם');
                      const completedTasks = personalTasks.filter(t => t.status === 'הושלם');
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeTasks.length} פעילות
                          </div>
                          <div className="text-center py-1 text-purple-600">
                            {completedTasks.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-lg font-medium mb-4">פעולות מהירות</h3>
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => setShowNewProject(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <FolderOpen size={16} />
                  פרויקט חדש
                </button>
                <button 
                  onClick={() => setShowNewTask(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <CheckSquare size={16} />
                  משימה חדשה
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    };

    return [managementTab, personalTab];
  })();

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">לוח בקרה - סמנכ"ל</h1>
      <div className="mb-8">
  <TabsComponent tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
</div>

{renderInlineContent()}
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard 
          title="סך הכל פרויקטים" 
          value={filteredDashboardProjects.length} 
          icon={FolderOpen} 
          color="blue"
        />
        <StatCard 
          title="סך הכל משימות" 
          value={filteredDashboardTasks.length} 
          icon={CheckSquare} 
          color="green"
        />
        <StatCard 
          title="לא אושרו" 
          value={unacknowledgedTasks.length} 
          icon={Clock} 
          color="yellow"
        />
        <StatCard 
          title="בסיכון/חסום" 
          value={atRiskTasks.length} 
          icon={AlertTriangle} 
          color="red"
        />
      </div>
{/* Daily Routines Section */}
<DailyRoutinesWidget />
{/* הערות חדשות */}
          <CombinedCommentsWidget />

      {/* Day Opening & Summary Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-purple-500 rounded-lg flex items-center justify-center">
              <Sun className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">פתיחה וסיכום יום</h2>
              <p className="text-sm text-gray-600">נהל את היום שלך בצורה מסודרת</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/day-opening')}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2 text-sm font-medium"
            >
              <Sun className="w-4 h-4" />
              פתיחת יום
            </button>
            <button
              onClick={() => navigate('/day-summary')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm font-medium"
            >
              <Moon className="w-4 h-4" />
              סיכום יום
            </button>
            <button
              onClick={() => navigate('/team-summaries')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
            >
              <Users className="w-4 h-4" />
              סיכומי הארגון
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sun className="w-5 h-5 text-orange-600" />
              <h3 className="font-semibold text-orange-800">פתיחת יום</h3>
            </div>
            <p className="text-sm text-orange-700">
              תכנן את היום: יעדים, משימות מרכזיות, פגישות וחסמים צפויים
            </p>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Moon className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-purple-800">סיכום יום</h3>
            </div>
            <p className="text-sm text-purple-700">
              סכם את היום: הישגים, אתגרים, תובנות ותכנון למחר
            </p>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-blue-800">סיכומי הארגון</h3>
            </div>
            <p className="text-sm text-blue-700">
              צפה בפתיחות וסיכומי יום של כל העובדים בארגון
            </p>
          </div>
        </div>
      </div>


      

      {/* Inline Content Display */}
      {renderInlineContent()}

      {/* Modals */}
      {/* Edit Project Modal */}
      {showEditProject && editProject && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowEditProject(false);
            setEditProject(null);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowEditProject(false);
                  setEditProject(null);
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">ערוך פרויקט</h3>
                <p className="text-blue-100 text-sm">ערוך את פרטי הפרויקט</p>
              </div>
              <button
                type="submit"
                form="editProjectForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="editProjectForm" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await updateProject(editProject.id, editProject);
                setShowEditProject(false);
                setEditProject(null);
              } catch (error) {
                console.error('Error updating project:', error);
              }
            }} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column - Basic Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-gray-500" />
                      שם הפרויקט
                    </label>
                    <input 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                      placeholder="שם הפרויקט" 
                      value={editProject.name} 
                      onChange={(e) => setEditProject({...editProject, name: e.target.value})} 
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      תיאור הפרויקט
                    </label>
                    <textarea 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all h-24 resize-none" 
                      placeholder="תיאור הפרויקט" 
                      value={editProject.description} 
                      onChange={(e) => setEditProject({...editProject, description: e.target.value})} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-500" />
                      תחום
                    </label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      value={editProject.scope}
                      onChange={(e) => setEditProject({...editProject, scope: e.target.value})}
                    >
                      <option value="personal">אישי</option>
                      <option value="department">מחלקה</option>
                      <option value="organization">ארגון</option>
                    </select>
                  </div>
                </div>
                
                {/* Right Column - Additional Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      בחר מחלקה
                    </label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      value={editProject.departmentId}
                      onChange={(e) => setEditProject({...editProject, departmentId: e.target.value})}
                    >
                      <option value="">בחר מחלקה</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        תאריך התחלה
                      </label>
                      <input 
                        type="date" 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                        placeholder="תאריך התחלה" 
                        value={editProject.startDate} 
                        onChange={(e) => setEditProject({...editProject, startDate: e.target.value})} 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        תאריך סיום
                      </label>
                      <input 
                        type="date" 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                        placeholder="תאריך סיום" 
                        value={editProject.endDate} 
                        onChange={(e) => setEditProject({...editProject, endDate: e.target.value})} 
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => {
                    setShowEditProject(false);
                    setEditProject(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  שמור שינויים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditTask && editTask && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowEditTask(false);
            setEditTask(null);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-purple-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowEditTask(false);
                  setEditTask(null);
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">ערוך משימה</h3>
                <p className="text-purple-100 text-sm">ערוך את פרטי המשימה</p>
              </div>
              <button
                type="submit"
                form="editTaskForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="editTaskForm" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await updateTask(editTask.id, editTask);
                setShowEditTask(false);
                setEditTask(null);
              } catch (error) {
                console.error('Error updating task:', error);
              }
            }} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column - Basic Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      כותרת המשימה
                    </label>
                    <input 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" 
                      placeholder="כותרת המשימה" 
                      value={editTask.title} 
                      onChange={(e) => setEditTask({...editTask, title: e.target.value})} 
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      תיאור המשימה
                    </label>
                    <textarea 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all h-24 resize-none" 
                      placeholder="תיאור המשימה" 
                      value={editTask.description} 
                      onChange={(e) => setEditTask({...editTask, description: e.target.value})} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-gray-500" />
                      עדיפות
                    </label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                      value={editTask.priority}
                      onChange={(e) => setEditTask({...editTask, priority: e.target.value})}
                    >
                      <option value="important_urgent">חשוב ודחוף</option>
                      <option value="important_not_urgent">חשוב ולא דחוף</option>
                      <option value="not_important_urgent">דחוף ולא חשוב</option>
                      <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
                    </select>
                  </div>
                </div>
                
                {/* Right Column - Additional Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      דדליין
                    </label>
                    <input 
                      type="date" 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" 
                      placeholder="דדליין" 
                      value={editTask.dueDate} 
                      onChange={(e) => setEditTask({...editTask, dueDate: e.target.value})} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      הערכת שעות
                    </label>
                    <input 
                      type="number" 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" 
                      placeholder="הערכת שעות" 
                      value={editTask.estimate} 
                      onChange={(e) => setEditTask({...editTask, estimate: parseInt(e.target.value) || 0})} 
                    />
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => {
                    setShowEditTask(false);
                    setEditTask(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  שמור שינויים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Team Modal */}
      {showNewTeam && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowNewTeam(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-orange-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => setShowNewTeam(false)}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">צור צוות חדש</h3>
                <p className="text-orange-100 text-sm">הוסף צוות חדש למערכת</p>
              </div>
              <button
                type="submit"
                form="newTeamForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Plus className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="newTeamForm" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await createTeam(newTeam);
                setShowNewTeam(false);
                setNewTeam({
                  name: '',
                  description: '',
                  departmentId: '',
                  managerId: '',
                  memberIds: [],
                  isActive: true
                });
              } catch (error) {
                console.error('Error creating team:', error);
              }
            }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">שם הצוות</label>
                  <input 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" 
                    placeholder="שם הצוות" 
                    value={newTeam.name} 
                    onChange={(e) => setNewTeam({...newTeam, name: e.target.value})} 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">תיאור</label>
                  <textarea 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 h-24 resize-none" 
                    placeholder="תיאור הצוות" 
                    value={newTeam.description} 
                    onChange={(e) => setNewTeam({...newTeam, description: e.target.value})} 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">מחלקה</label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      value={newTeam.departmentId}
                      onChange={(e) => setNewTeam({...newTeam, departmentId: e.target.value})}
                    >
                      <option value="">בחר מחלקה</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">מנהל צוות</label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      value={newTeam.managerId}
                      onChange={(e) => setNewTeam({...newTeam, managerId: e.target.value})}
                    >
                      <option value="">בחר מנהל</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">חברי צוות</label>
                  <select 
                    multiple
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 h-32"
                    value={newTeam.memberIds}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      setNewTeam({...newTeam, memberIds: selectedOptions});
                    }}
                  >
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">החזק Ctrl (או Cmd) לבחירה מרובה</p>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="teamActive"
                    checked={newTeam.isActive}
                    onChange={(e) => setNewTeam({...newTeam, isActive: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="teamActive" className="text-sm text-gray-700">צוות פעיל</label>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => setShowNewTeam(false)}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  צור צוות
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditTeam && editTeam && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowEditTeam(false);
            setEditTeam(null);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowEditTeam(false);
                  setEditTeam(null);
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">ערוך צוות</h3>
                <p className="text-blue-100 text-sm">ערוך את פרטי הצוות</p>
              </div>
              <button
                type="submit"
                form="editTeamForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="editTeamForm" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await updateTeam(editTeam.id, editTeam);
                setShowEditTeam(false);
                setEditTeam(null);
              } catch (error) {
                console.error('Error updating team:', error);
              }
            }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">שם הצוות</label>
                  <input 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                    placeholder="שם הצוות" 
                    value={editTeam.name} 
                    onChange={(e) => setEditTeam({...editTeam, name: e.target.value})} 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">תיאור</label>
                  <textarea 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24 resize-none" 
                    placeholder="תיאור הצוות" 
                    value={editTeam.description} 
                    onChange={(e) => setEditTeam({...editTeam, description: e.target.value})} 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">מחלקה</label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={editTeam.departmentId}
                      onChange={(e) => setEditTeam({...editTeam, departmentId: e.target.value})}
                    >
                      <option value="">בחר מחלקה</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">מנהל צוות</label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={editTeam.managerId}
                      onChange={(e) => setEditTeam({...editTeam, managerId: e.target.value})}
                    >
                      <option value="">בחר מנהל</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">חברי צוות</label>
                  <select 
                    multiple
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-32"
                    value={editTeam.memberIds || []}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      setEditTeam({...editTeam, memberIds: selectedOptions});
                    }}
                  >
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">החזק Ctrl (או Cmd) לבחירה מרובה</p>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="editTeamActive"
                    checked={editTeam.isActive}
                    onChange={(e) => setEditTeam({...editTeam, isActive: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="editTeamActive" className="text-sm text-gray-700">צוות פעיל</label>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => {
                    setShowEditTeam(false);
                    setEditTeam(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  שמור שינויים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Task Modal */}
      {showNewTask && (
        <SimpleTaskForm
          isOpen={showNewTask}
          onClose={() => setShowNewTask(false)}
          onSubmit={async (taskData) => {
            await createTask(taskData);
            setShowNewTask(false);
          }}
          currentUser={currentUser}
          users={users}
          projects={projects}
        />
      )}
    </div>
  );
};

export default DeputyCeoDashboard;